<!--
USE THE FOLLOWING TO GENERATE THE IFRAME:
<iframe src="IFRAME-URL-GOES-HERE" allowTransparency="true" frameborder="0"
style="display:block;position:fixed;top:0;bottom:0;left:0;right:0;width:100%;
height:100%;border:none;z-index:2147483647;"></iframe>
-->

<template>
  <div id="app" :style="cssProps">
    <div class="container-fluid p-4" :class="{ fullscreen: isFullscreen }">
      <!-- show if loading -->
      <div v-show="isLoading" class="loader-on-white">
        <Spinner v-bind:isLoading="isLoading"/>
      </div>
      <div v-show="!isLoading" class="mock-modal bg-white">
        <Header v-if="!isFullscreen"/>
        <main role="main">
          <router-view/>
        </main>

        <Footer v-if="!isFullscreen"/>
      </div>
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import Header from '@/components/Header.vue';
import Footer from '@/components/Footer.vue';
import { mapState } from 'vuex';
import { initializeSegment } from '@/thirdPartyLibraries/segment/segmentio';
import postMessageToWindow from '@/api/postMessageToWindow';
import Spinner from '@/components/Spinner.vue';

/**
 * App
 *
 * The main entry point into the project, this is the component that houses the
 * entire frontend application.
 */
export default {
  name: 'App',
  components: {
    Header,
    Footer,
    Spinner,
  },
  data() {
    return {
      isLoading: true,
    };
  },
  computed: {
    ...mapState('siteSettings', ['settings']),
    cssProps() {
      const buttonStyles = {
        '--initial-button-color': this.settings.styles.coloredButtons?.initialButtonColor,
        '--initial-button-text-color': this.settings.styles.coloredButtons?.initialTextColor,
        '--hover-button-color': this.settings.styles.coloredButtons?.hoverButtonColor,
        '--hover-button-text-color': this.settings.styles.coloredButtons?.hoverButtonTextColor,
        '--outlined-button-color': this.settings.styles.coloredButtons?.outlinedButtonColor,
        '--outlined-button-text-color': this.settings.styles.coloredButtons?.outlinedButtonTextColor,
      };
      const linkStyles = {
        '--initial-link-color': this.settings.styles.linkColor?.initialColor,
        '--hover-link-color': this.settings.styles.linkColor?.hoverColor,
      };
      const attentionMessageStyles = {
        '--attention-message-background-color': this.settings.styles.attentionMessageColor?.backgroundColor,
        '--attention-message-text-color': this.settings.styles.attentionMessageColor?.textColor,
        '--attention-message-border-color': this.settings.styles.attentionMessageColor?.borderColor,
      };
      return { ...buttonStyles, ...linkStyles, ...attentionMessageStyles };
    },
    isFullscreen() {
      return (/true/).test(this.$route?.query.fullScreen);
    },

    routeName() {
      return this.$route.name;
    },
  },
  watch: {
    settings(newSettings) {
      const writeKey = newSettings.segment?.write_key;
      // We want to initialize segment once we get
      // our write key from the site settings.
      if (writeKey) {
        const anonymousId = this.$route.query.suid;
        initializeSegment(writeKey);
        window.analytics?.setAnonymousId(anonymousId);
      }
      this.isLoading = false;
    },
    $route(to, from) {
      this.sendRouteChange(to.name, from.name);
    },
  },

  methods: {
    /**
     * the callback from ResizeObserver returns the element in array of 1,
     * send the height of the body using postMessage.
     */
    sendHeight(entries) {
      if (entries && entries.length) {
        const data = {
          close: false,
          reason: 'iframeHeightChange',
          data: {
            frameHeight: entries[0].contentRect?.height,
          },
        };
        postMessageToWindow(data);
      }
    },

    /**
     * Sends a post message and tracks the event whenever the user has
     * closed the modal via the ESC key
     */
    sendEscape() {
      const data = {
        close: true,
        reason: 'dismiss',
        location: this.routeName,
        data: {
          hasShippingData: this.$store.state.hasShippingData,
        },
      };
      postMessageToWindow(data);
    },

    /**
     * Sends a post message whenever there has been a route change
     */
    sendRouteChange(toRoute, fromRoute) {
      const data = {
        close: false,
        reason: 'pageChange',
        location: fromRoute,
        data: {
          nextRoute: toRoute,
        },
      };
      postMessageToWindow(data);
    },

    /**
     * Use the ResizeObserver to watch the height changes on body.
     */
    sizeObserver() {
      const ro = new ResizeObserver(this.sendHeight);
      ro.observe(document.body);
    },

    /**
     * Check if parent needs to watch height change or not
     */
    watchFrameHeight() {
      return this.$route?.query?.watchFrameHeight === 'true';
    },
  },

  mounted() {
    if (this.watchFrameHeight()) this.sizeObserver();

    window.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        this.sendEscape();
      }
    });
  },
};
</script>

<style lang="scss">
  @import '/assets/scss/clients/enervee/enervee-marketplace.scss';
</style>
